import { LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Logger } from '../../../core/logger';
import { SerializableElement } from '../../../core/serializable-element';

interface ElementLoader extends HTMLElement {
  loadedElement(): SerializableElement | null;
}

interface FormElement extends HTMLElement {
  elementLoaders?(): ElementLoader[];
}

interface ValidatableElement {
  validate?(): { isValid: boolean };
  invalid?(): void;
  valid?(): void;
  scrollIntoView?(options: ScrollIntoViewOptions): void;
}

@customElement('form-base')
export class FormBase extends LitElement {
  @property({ type: Number, reflect: true })
  pos = 0;

  @property({ type: Array })
  sections: any[] = [];

  @property({ type: String, reflect: true })
  head = '';

  async serialize(): Promise<Record<string, any>> {
    const elements = this.findElements();
    const json: Record<string, any> = {};

    for (const loader of elements) {
      const element = loader.loadedElement();
      
      
      if (element && this.isSerializableElement(element)) {
        try {
          const value = await Promise.resolve(element.getValue());
          this.processValue(json, element.getName(), value);
        } catch (error) {
          Logger.i.error(`Serialization error for ${element.getName()}:`, error);
        }
      }
    }
    
    return json;
  }

  private processValue(json: Record<string, any>, name: string, value: any): void {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.entries(value).forEach(([key, val]) => {
        this.addSerializedElement(json, key, val);
      });
    } else {
      this.addSerializedElement(json, name, value);
    }
  }

  validate(): boolean {
    const elements = this.findElements();
    return Array.from(elements).every(loader => {
      const element = loader.loadedElement();
      // Skip if no element or element doesn't have validate method
      if (!element || typeof element.validate !== 'function') {
        console.warn('Element missing validate method:', element);
        return true; // Skip validation for non-validatable elements
      }

      try {
        const validation = element.validate();
        
        // Handle different validation return types
        let isValid = false;
        
        if (typeof validation === 'boolean') {
          isValid = validation;
        } else if (validation && typeof validation === 'object' && 'isValid' in validation) {
          isValid = validation.isValid;
        } else {
          console.warn('Invalid validation return type:', validation);
          isValid = true; // Default to valid if we can't determine
        }
        
        if (!isValid) {
          this.handleInvalidElement(loader, element as ValidatableElement);
          return false;
        } else {
          this.handleValidElement(loader, element as ValidatableElement);
          return true;
        }
      } catch (error) {
        Logger.i.error('Validation error:', error);
        console.error('Validation failed for element:', element, error);
        return false;
      }
    });
  }

  private handleInvalidElement(loader: ElementLoader, element: ValidatableElement): void {
    try {
      if (typeof element.invalid === 'function') {
        element.invalid();
      } else {
        loader.classList.add('invalid-e-l');
      }
    } catch (error) {
      loader.classList.add('invalid-e-l');
      console.error('Error calling invalid():', error);
    }
    
    // Scroll to element if possible
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else if (loader.scrollIntoView) {
      loader.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  private handleValidElement(loader: ElementLoader, element: ValidatableElement): void {
    try {
      if (typeof element.valid === 'function') {
        element.valid();
      } else {
        loader.classList.remove('invalid-e-l');
      }
    } catch (error) {
      loader.classList.remove('invalid-e-l');
      console.error('Error calling valid():', error);
    }
  }

  private addSerializedElement(json: Record<string, any>, name: string, value: any): void {
    if (json[name] === undefined) {
      json[name] = value;
    } else {
      json[name] = Array.isArray(json[name]) ? [...json[name], value] : [json[name], value];
    }
  }

  private getForm(): FormElement | null {
    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="body"]') || this.shadowRoot?.querySelector<HTMLSlotElement>('e-list[slot="body"]');
    console.log("slot is ",slot)
    const assignedElements = slot?.assignedElements({ flatten: true });

    return assignedElements?.[0] as FormElement || null;
  }

private findElements(): ElementLoader[] {
  const formElement = this.getForm();

  const loaders = formElement?.elementLoaders?.() || [];
    
  // Convert NodeList to array if needed and filter out null/undefined
  let result: ElementLoader[] = [];
  
  if (loaders && typeof loaders === 'object') {
    // Check if it's a NodeList (has length and item method)
    if ('length' in loaders && typeof (loaders as any).item === 'function') {
      result = Array.from(loaders as any);
    } 
    // Check if it's array-like (has length and numeric indices)
    else if ('length' in loaders && typeof loaders.length === 'number') {
      result = Array.from(loaders as any);
    }
    // If it's already an array
    else if (Array.isArray(loaders)) {
      result = loaders;
    }
  }
  
  // Filter out any null/undefined loaders
  return result.filter(loader => loader != null);
}

  private isSerializableElement(element: any): element is SerializableElement {
    return element && 
           typeof element.getValue === 'function' && 
           typeof element.getName === 'function';
  }
}